import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Container, Card, Row, Col, ListGroup } from "react-bootstrap";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
// import "";

// Utility function to format ISO date to readable time (e.g., 2:30 PM)
const formatTime = (isoDateString) => {
  if (!isoDateString) return "N/A";
  const value = formatIsoToDateTime(isoDateString);
  const date = new Date(value);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

// Utility function to format activity time (e.g., 2025-04-21 14:09:11 to 2:09 PM)
const formatActivityTime = (timeString) => {
  if (!timeString) return "N/A";
  const date = new Date(timeString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

// Utility function to format seconds to HH:mm:ss
const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

// Utility function to format ISO date string
const formatIsoToDateTime = (isoDateString) => {
  try {
    if (!isoDateString || typeof isoDateString !== "string") {
      return "-";
    }
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) {
      return "-";
    }
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error("Error formatting date:", error.message);
    return "-";
  }
};

// Mock data to simulate API response
const mockRouteinData = {
  status: "present",
  attendance: {
    punch_time_in: "2025-04-21T09:00:00Z",
    punch_time_out: "2025-04-21T17:00:00Z",
    punch_in_location: "Office A",
    punch_out_location: "Office A",
    check_in_verify_type: "Face",
    check_out_verify_type: "PIN",
  },
  working_time: "08:00:00",
  break_time: "01:00:00",
  break_count: 2,
  activity: [
    {
      id: 1,
      activity_type: "Check-in",
      activity_time: "2025-04-21T09:00:00Z",
      no_seconds: 0,
    },
    {
      id: 2,
      activity_type: "Break Time",
      activity_time: "2025-04-21T12:00:00Z",
      no_seconds: 3600,
    },
    {
      id: 3,
      activity_type: "Working",
      activity_time: "2025-04-21T13:00:00Z",
      no_seconds: 14400,
    },
  ],
};

const RouteinDetail = () => {
  const location = useLocation();
  const { emp_id, date } = location.state || {};
  const [routeinData, setRouteinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Simulate data fetching with mock data
    if (!emp_id || !date) {
      setError("Missing employee ID or date.");
      setLoading(false);
      return;
    }

    // Simulate async operation (e.g., API call)
    setTimeout(() => {
      setRouteinData(mockRouteinData);
      setLoading(false);
    }, 500); // Simulate network delay
  }, [emp_id, date]);

  // Render activity item for the list
  const renderActivityItem = (item) => {
    const getActivityIcon = (type) => {
      switch (type) {
        case "Check-in":
          return <Icon icon="entypo:controller-play" width={24} height={24} color="#fff" />;
        case "Break Time":
          return <Icon icon="material-symbols:coffee-outline" width={24} height={24} color="#fff" />;
        case "Working":
          return <Icon icon="material-symbols:truck-delivery" width={24} height={24} color="#fff" />;
        default:
          return <Icon icon="material-symbols:info-outline" width={24} height={24} color="#fff" />;
      }
    };

    return (
      <ListGroup.Item key={item.id} className="activity-item">
        <div className="activity-icon">{getActivityIcon(item.activity_type)}</div>
        <div className="activity-details">
          <div className="activity-type">{item.activity_type}</div>
          <div className="activity-time">{formatActivityTime(item.activity_time)}</div>
          <div className="activity-duration">Duration: {formatDuration(item.no_seconds)}</div>
        </div>
      </ListGroup.Item>
    );
  };

  if (loading) {
    return (
      <MasterLayout>
        <Container className="text-center mt-5">
          <div>Loading...</div>
        </Container>
      </MasterLayout>
    );
  }

  if (error) {
    return (
      <MasterLayout>
        <Container className="mt-5">
          <div className="alert alert-danger">{error}</div>
        </Container>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title="Route-in Detail" />
      <div className="routein-detail-container">
        <Container fluid className="p-4">
          {/* Attendance Card */}
          <Card className="attendance-card">
            <div className="card-header">
              <h5 className="card-title">Attendance Summary</h5>
              <Icon
                icon={
                  routeinData?.status === "present"
                    ? "material-symbols:check-circle"
                    : "material-symbols:alert-circle"
                }
                width={28}
                height={28}
                color={routeinData?.status === "present" ? "#0f0" : "#f00"}
              />
            </div>
            <Card.Body>
              <Row className="card-row">
                <Col xs={1}>
                  <Icon icon="entypo:clock" width={24} height={24} color="#fff" />
                </Col>
                <Col>
                  <div className="card-text">
                    Punch In: {formatTime(routeinData?.attendance?.punch_time_in)}
                  </div>
                </Col>
              </Row>
              <Row className="card-row">
                <Col xs={1}>
                  <Icon icon="entypo:clock" width={24} height={24} color="#fff" />
                </Col>
                <Col>
                  <div className="card-text">
                    Punch Out: {formatTime(routeinData?.attendance?.punch_time_out)}
                  </div>
                </Col>
              </Row>
              <Row className="card-row">
                <Col xs={1}>
                  <Icon icon="material-symbols:location-on" width={24} height={24} color="#fff" />
                </Col>
                <Col>
                  <div className="card-text">
                    In Location: {routeinData?.attendance?.punch_in_location || "N/A"}
                  </div>
                </Col>
              </Row>
              <Row className="card-row">
                <Col xs={1}>
                  <Icon icon="material-symbols:location-on" width={24} height={24} color="#fff" />
                </Col>
                <Col>
                  <div className="card-text">
                    Out Location: {routeinData?.attendance?.punch_out_location || "N/A"}
                  </div>
                </Col>
              </Row>
              <Row className="card-row">
                <Col xs={1}>
                  <Icon icon="material-symbols:lock" width={24} height={24} color="#fff" />
                </Col>
                <Col>
                  <div className="card-text">
                    Verification: {routeinData?.attendance?.check_in_verify_type || "N/A"} (In),{" "}
                    {routeinData?.attendance?.check_out_verify_type || "N/A"} (Out)
                  </div>
                </Col>
              </Row>
              <div className="summary-section">
                <h6 className="summary-title">Shift Metrics</h6>
                <Row className="summary-row">
                  <Col xs={1}>
                    <Icon icon="material-symbols:access-time" width={20} height={20} color="#fff" />
                  </Col>
                  <Col>
                    <div className="summary-text">
                      Working Time: {routeinData?.working_time || "N/A"}
                    </div>
                  </Col>
                </Row>
                <Row className="summary-row">
                  <Col xs={1}>
                    <Icon icon="material-symbols:coffee-outline" width={20} height={20} color="#fff" />
                  </Col>
                  <Col>
                    <div className="summary-text">
                      Break Time: {routeinData?.break_time || "N/A"}
                    </div>
                  </Col>
                </Row>
                <Row className="summary-row">
                  <Col xs={1}>
                    <Icon icon="material-symbols:counter-1" width={20} height={20} color="#fff" />
                  </Col>
                  <Col>
                    <div className="summary-text">
                      Break Count: {routeinData?.break_count || "0"}
                    </div>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>

          {/* Activity List */}
          <div className="activity-container">
            <h5 className="summary-title">Activity Log</h5>
            <ListGroup>
              {routeinData?.activity && routeinData.activity.length > 0 ? (
                routeinData.activity.map((item) => renderActivityItem(item))
              ) : (
                <ListGroup.Item>No activities found.</ListGroup.Item>
              )}
            </ListGroup>
          </div>
        </Container>
      </div>
    </MasterLayout>
  );
};

export default RouteinDetail;